import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search, Edit, Trash2, Phone, Mail, Calendar, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { patientsApi } from '../services/api';
import { Patient } from '../types';
import PatientModal from '../components/PatientModal';

type SortField = 'name' | 'email' | 'phone' | 'age' | 'lastVisit' | 'totalVisits';
type SortDirection = 'asc' | 'desc';

const Patients: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Fetch patients
  const { data: patientsResponse, isLoading } = useQuery(
    ['patients', currentPage, searchTerm],
    () => patientsApi.getAll({ 
      page: currentPage, 
      limit: pageSize, 
      search: searchTerm 
    }),
    {
      keepPreviousData: true,
    }
  );

  const patients = patientsResponse?.data?.patients || [];
  // Normalize response shapes: some responses might return the body at top-level
  const responseData: any = patientsResponse?.data;
  const patientsNormalized = responseData?.patients ?? (Array.isArray(responseData) ? responseData : []);
  const totalPages = responseData?.totalPages ?? (patientsResponse as any)?.totalPages ?? 0;
  const total = responseData?.total ?? (patientsResponse as any)?.total ?? 0;



  // Debug: log raw response to help diagnose why the list might be empty
  useEffect(() => {
    if (patientsResponse) {
      // eslint-disable-next-line no-console
      console.debug('patientsResponse:', patientsResponse);
    }
  }, [patientsResponse]);

  // Create patient mutation
  const createPatientMutation = useMutation(
    (data: Partial<Patient>) => patientsApi.create(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('patients');
        queryClient.invalidateQueries('patients-count');
        setIsModalOpen(false);
        setSelectedPatient(null);
      },
    }
  );

  // Update patient mutation
  const updatePatientMutation = useMutation(
    ({ id, data }: { id: string; data: Partial<Patient> }) =>
      patientsApi.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('patients');
        queryClient.invalidateQueries('patients-count');
        setIsModalOpen(false);
        setSelectedPatient(null);
      },
    }
  );

  // Delete patient mutation
  const deletePatientMutation = useMutation(
    (id: string) => patientsApi.delete(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('patients');
        queryClient.invalidateQueries('patients-count');
      },
    }
  );

  const handleCreatePatient = () => {
    setSelectedPatient(null);
    setIsModalOpen(true);
  };

  const handleEditPatient = (patient: Patient) => {
    setSelectedPatient(patient);
    setIsModalOpen(true);
  };

  const handleDeletePatient = async (patient: Patient) => {
    if (window.confirm(`¿Estás seguro de que quieres eliminar a ${patient.firstName} ${patient.lastName}?`)) {
      await deletePatientMutation.mutateAsync(patient._id);
    }
  };

  const handleSavePatient = async (data: Partial<Patient>) => {
    if (selectedPatient) {
      await updatePatientMutation.mutateAsync({
        id: selectedPatient._id,
        data,
      });
    } else {
      await createPatientMutation.mutateAsync(data);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedPatient(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getAge = (dateOfBirth: string) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-dental-600" />
      : <ArrowDown className="h-4 w-4 text-dental-600" />;
  };

  const sortedPatients = useMemo(() => {
    if (!sortField) return patientsNormalized;

    return [...patientsNormalized].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'name':
          aValue = `${a.firstName} ${a.lastName}`.toLowerCase();
          bValue = `${b.firstName} ${b.lastName}`.toLowerCase();
          break;
        case 'email':
          aValue = a.email.toLowerCase();
          bValue = b.email.toLowerCase();
          break;
        case 'phone':
          aValue = a.phone;
          bValue = b.phone;
          break;
        case 'age':
          aValue = getAge(a.dateOfBirth);
          bValue = getAge(b.dateOfBirth);
          break;
        case 'lastVisit':
          aValue = a.dentalHistory?.lastVisit ? new Date(a.dentalHistory.lastVisit).getTime() : 0;
          bValue = b.dentalHistory?.lastVisit ? new Date(b.dentalHistory.lastVisit).getTime() : 0;
          break;
        case 'totalVisits':
          aValue = a.dentalHistory?.totalVisits || 0;
          bValue = b.dentalHistory?.totalVisits || 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [patientsNormalized, sortField, sortDirection]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pacientes</h1>
          <p className="text-gray-600">Gestiona la información de los pacientes</p>
        </div>
        <button
          onClick={handleCreatePatient}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-dental-600 hover:bg-dental-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dental-500"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Paciente
        </button>
      </div>

      {/* Search and filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar pacientes por nombre, email o teléfono..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dental-500 focus:border-dental-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Patients table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner"></div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center justify-between">
                        Paciente
                        {getSortIcon('name')}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('email')}
                    >
                      <div className="flex items-center justify-between">
                        Contacto
                        {getSortIcon('email')}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('age')}
                    >
                      <div className="flex items-center justify-between">
                        Edad
                        {getSortIcon('age')}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('lastVisit')}
                    >
                      <div className="flex items-center justify-between">
                        Última Visita
                        {getSortIcon('lastVisit')}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('totalVisits')}
                    >
                      <div className="flex items-center justify-between">
                        Total Visitas
                        {getSortIcon('totalVisits')}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedPatients.map((patient: Patient) => (
                    <tr key={patient._id} className="hover:bg-dental-50 cursor-pointer transition-colors duration-200">
                      <td 
                        className="px-6 py-4 whitespace-nowrap"
                        onClick={() => navigate(`/patients/${patient._id}`)}
                      >
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-dental-100 flex items-center justify-center">
                              <span className="text-sm font-medium text-dental-600">
                                {patient.firstName[0]}{patient.lastName[0]}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {patient.firstName} {patient.lastName}
                            </div>
                            <div className="text-sm text-gray-500">
                              ID: {patient._id.slice(-8)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td 
                        className="px-6 py-4 whitespace-nowrap"
                        onClick={() => navigate(`/patients/${patient._id}`)}
                      >
                        <div className="text-sm text-gray-900">
                          <div className="flex items-center">
                            <Mail className="h-4 w-4 text-gray-400 mr-2" />
                            {patient.email}
                          </div>
                          <div className="flex items-center mt-1">
                            <Phone className="h-4 w-4 text-gray-400 mr-2" />
                            {patient.phone}
                          </div>
                        </div>
                      </td>
                      <td 
                        className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                        onClick={() => navigate(`/patients/${patient._id}`)}
                      >
                        {getAge(patient.dateOfBirth)} años
                      </td>
                      <td 
                        className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                        onClick={() => navigate(`/patients/${patient._id}`)}
                      >
                        {patient.dentalHistory?.lastVisit 
                          ? formatDate(patient.dentalHistory.lastVisit)
                          : 'Nunca'
                        }
                      </td>
                      <td 
                        className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                        onClick={() => navigate(`/patients/${patient._id}`)}
                      >
                        {patient.dentalHistory?.totalVisits || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <Link
                            to={`/patients/${patient._id}`}
                            className="text-dental-600 hover:text-dental-900"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Calendar className="h-4 w-4" />
                          </Link>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditPatient(patient);
                            }}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeletePatient(patient);
                            }}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Siguiente
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Mostrando{' '}
                      <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span>
                      {' '}a{' '}
                      <span className="font-medium">
                        {Math.min(currentPage * pageSize, total)}
                      </span>
                      {' '}de{' '}
                      <span className="font-medium">{total}</span>
                      {' '}resultados
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Anterior
                      </button>
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const page = i + 1;
                        return (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              currentPage === page
                                ? 'z-10 bg-dental-50 border-dental-500 text-dental-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      })}
                      <button
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Siguiente
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {isModalOpen && (
        <PatientModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSave={handleSavePatient}
          patient={selectedPatient}
          isLoading={
            createPatientMutation.isLoading ||
            updatePatientMutation.isLoading
          }
        />
      )}
    </div>
  );
};

export default Patients;















